import React, { useEffect, useState, useRef } from "react";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import Select from "react-select";
import citiesData from "./misc/cities.json";
import LocationPicker from "./LocationPicker";
import "./css/bleep-form.css";

// react-select renders its own DOM, so it needs the light values explicitly.
// Kept in sync with the --bl-* scale in src/styles/bleep-theme.css.
const LIGHT = {
  surface: "#ffffff",
  surface2: "#f8e9de",
  surface3: "#f0e0d6",
  text: "#24120b",
  textMuted: "#66554c",
  textSubtle: "#77665e",
  border: "#efe0d4",
  borderStrong: "#ddc9ba",
  brand: "#e66b2c",
};

const citySelectStyles = {
  // Matches .bleep-cat-trigger and .bleep-input-group exactly: white surface,
  // one hairline, and focus expressed as an outline rather than a ring layered
  // on top of a border that also changes colour.
  control: (base, state) => ({
    ...base,
    height: 50,
    minHeight: 50,
    backgroundColor: LIGHT.surface,
    borderRadius: 12,
    border: `1px solid ${state.isFocused ? LIGHT.brand : LIGHT.borderStrong}`,
    boxShadow: "none",
    outline: state.isFocused ? `2px solid ${LIGHT.brand}` : "none",
    outlineOffset: 2,
    cursor: "text",
    transition: "border-color 150ms ease",
    "&:hover": { borderColor: LIGHT.borderStrong },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 14px" }),
  singleValue: (base) => ({
    ...base,
    color: LIGHT.text,
    fontSize: 15,
  }),
  placeholder: (base) => ({
    ...base,
    color: LIGHT.textSubtle,
    fontSize: 15,
  }),
  input: (base) => ({
    ...base,
    color: LIGHT.text,
    fontSize: 15,
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: LIGHT.surface,
    borderRadius: 12,
    border: `1px solid ${LIGHT.border}`,
    boxShadow: "0 6px 16px rgba(16, 24, 40, 0.1)",
    overflow: "hidden",
    // Above .bleep-cat-panel (40) so the two dropdowns cannot fight.
    zIndex: 50,
  }),
  menuList: (base) => ({ ...base, padding: 6 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? LIGHT.surface3
      : state.isFocused
        ? LIGHT.surface2
        : "transparent",
    color: LIGHT.text,
    borderRadius: 8,
    fontSize: 15,
    cursor: "pointer",
    padding: "10px 12px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  clearIndicator: (base) => ({
    ...base,
    color: LIGHT.textSubtle,
    cursor: "pointer",
    "&:hover": { color: LIGHT.text },
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: LIGHT.textMuted,
    "&:hover": { color: LIGHT.text },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: LIGHT.textSubtle,
    fontSize: 14,
  }),
};

const CATEGORIES = [
  "Agriculture",
  "Apparel",
  "Automobiles",
  "Banking",
  "Beverages",
  "Chemicals",
  "Construction",
  "Cosmetics",
  "Currencies",
  "Education",
  "Electronics",
  "Food",
  "Government",
  "Media",
  "Organizations",
  "Petroleum",
  "Pharmaceuticals",
  "Other",
];

/**
 * A numbered step heading. There were five of these, each written out as a div
 * carrying about thirty lines of inline style, so changing the look of one meant
 * changing it in five places and the file's own steps had already drifted apart.
 */
const Step = ({ n, title, hint }) => (
  <div className="bleep-step">
    <div className="bleep-step__head">
      <span className="bleep-step__num" aria-hidden="true">
        {n}
      </span>
      <h2 className="bleep-step__title">{title}</h2>
    </div>
    {hint && <p className="bleep-step__hint">{hint}</p>}
  </div>
);

const ReportForm = ({ domain, onPosted }) => {
  const [selectedImages, setSelectedImages] = useState(new Array(8).fill(null));

  // Validation used three alert() calls. On a page the browser dialog is worse
  // than it was in the modal: it hides the form it is complaining about, and it
  // cannot say which of the five steps needs attention.
  const [formError, setFormError] = useState("");
  const errorRef = useRef(null);

  const [submissionInProgress, setSubmissionInProgress] = useState(false);
  const [submissionProgress, setSubmissionProgress] = useState(0);
  const [currency, setCurrency] = useState("UGX");
  const [allCities, setAllCities] = useState([]);
  const [catOpen, setCatOpen] = useState(false);
  const catRef = useRef(null);
  const [locationPickerShow, setLocationPickerShow] = useState(false);
  const [pinnedLocation, setPinnedLocation] = useState(null);

  useEffect(() => {
    if (!catOpen) return;
    const handler = (e) => {
      if (catRef.current && !catRef.current.contains(e.target))
        setCatOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [catOpen]);
  // const [selectedCity, setSelectedCity] = useState(null);
  const [selectedCity, setSelectedCity] = useState(() => {
    const savedCity = localStorage.getItem("selectedCity");
    return savedCity ? JSON.parse(savedCity) : null;
  });

  useEffect(() => {
    if (domain === "ke") {
      setCurrency("KES");
      setAllCities(citiesData.Kenya);
    } else if (domain === "ug") {
      setCurrency("UGX");
      setAllCities(citiesData.Uganda);
    }
  }, [domain]);

  const handleCitySelect = (opt) => {
    const city = opt ? opt.value : null;
    setSelectedCity(city);
    setPinnedLocation(null);
    if (city) {
      localStorage.setItem("selectedCity", JSON.stringify(city));
    } else {
      localStorage.removeItem("selectedCity");
    }
  };

  const [formData, setFormData] = useState(() => {
    const savedFormData = localStorage.getItem("formData");
    return savedFormData
      ? JSON.parse(savedFormData)
      : { description: "", address: "", price: "", category: "" };
  });

  useEffect(() => {
    // save to cache
    localStorage.setItem("formData", JSON.stringify(formData));
  }, [formData]);

  const userId = Cookies.get("userId");
  const userEmail = Cookies.get("userEmail");

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    // Check if the input being changed is for the "price" field
    if (name === "price") {
      // Remove non-numeric characters from input value
      const numericValue = value.replace(/\D/g, "");

      // Format currency with commas
      const formattedValue = numericValue
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

      // Update the state with the formatted value
      setFormData((prevData) => ({
        ...prevData,
        [name]: formattedValue,
      }));
    } else {
      // For other fields, update the state without formatting
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  // Shorter than they were ("Batch No. & lot print", "Additional photo 1"),
  // because these have to read inside a square tile at 11px. They are the whole
  // point of the guided grid, so they have to be legible, not just present.
  const prompts = [
    "The product",
    "Packaging",
    "Barcode",
    "Batch or lot number",
    "Label",
    "Anything else",
    "Anything else",
    "Anything else",
  ];

  const photoCount = selectedImages.filter(Boolean).length;

  const handleFileChange = (index, event) => {
    const files = event.target.files;

    if (files.length > 0) {
      const newSelectedImages = [...selectedImages];
      const newFile = new File([files[0]], files[0].name, {
        type: files[0].type,
      }); // Create a new File object
      newSelectedImages[index] = newFile;
      setSelectedImages(newSelectedImages);
    }
  };

  const handleDeleteImage = (index) => {
    const newSelectedImages = [...selectedImages];
    newSelectedImages[index] = null;
    setSelectedImages(newSelectedImages);

    // Clear the file input value
    const fileInput = document.getElementById(`fileInput${index}`);
    if (fileInput) {
      fileInput.value = ""; // Clear the value to allow re-uploading the same file
    }
  };

  const postReport = (event) => {
    event.preventDefault();

    // Check if all fields are filled
    const description = event.target.description.value;
    const address = event.target.address.value;
    const price = event.target.price.value;
    const category = event.target.category.value;
    const country = event.target.country.value;

    let atLeastOneImageSelected = false;

    for (let i = 0; i < 8; i++) {
      if (selectedImages[i] !== null) {
        atLeastOneImageSelected = true;
        break;
      }
    }

    // Same three checks in the same order, shown in the page rather than in a
    // browser dialog, and each one now names the step it belongs to.
    const fail = (message) => {
      setFormError(message);
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    if (!atLeastOneImageSelected) {
      fail("Step 2: please upload at least one photo of the item.");
      return;
    }

    if (
      !description ||
      !address ||
      !selectedCity ||
      !price ||
      !category ||
      !country
    ) {
      fail("Please fill in every step before posting.");
      return;
    }

    if (!pinnedLocation) {
      fail("Step 4: please pin the exact location on the map.");
      return;
    }

    setFormError("");
    setSubmissionInProgress(true);

    const formData = new FormData();

    // Append each selected image to the formData object
    for (let i = 0; i < selectedImages.length; i++) {
      formData.append("postimages", selectedImages[i]);
    }

    // Append other form data fields
    formData.append("user", userId);
    formData.append("description", description);
    formData.append("address", address);
    formData.append("city", selectedCity);
    formData.append("price", price);
    formData.append("category", category);
    formData.append("country", country);
    formData.append("platform", "web");

    // Reuse the same device fingerprint set by the sign-in page
    const deviceId = localStorage.getItem("bleepWebDeviceId") || undefined;
    if (deviceId) formData.append("deviceId", deviceId);

    const ua = navigator.userAgent;
    let browser = "Unknown";
    if (/Edg\//.test(ua)) browser = "Edge";
    else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
    else if (/Chrome\//.test(ua)) browser = "Chrome";
    else if (/Firefox\//.test(ua)) browser = "Firefox";
    else if (/Safari\//.test(ua)) browser = "Safari";
    const versionKey = browser === "Edge" ? "Edg" : browser;
    const vMatch = ua.match(new RegExp(`${versionKey}\\/(\\d+)`));
    const model = vMatch ? `${browser} ${vMatch[1]}` : browser;

    let brand = "Unknown";
    if (/Windows/.test(ua)) brand = "Windows";
    else if (/Mac OS X/.test(ua)) brand = "macOS";
    else if (/Android/.test(ua)) brand = "Android";
    else if (/iPhone|iPad/.test(ua)) brand = "iOS";
    else if (/Linux/.test(ua)) brand = "Linux";

    formData.append("os", "web");
    formData.append("model", model);
    formData.append("brand", brand);
    formData.append(
      "deviceMetadata",
      JSON.stringify({
        userAgent: ua,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    );

    if (pinnedLocation) {
      formData.append("lat", pinnedLocation.lat);
      formData.append("lng", pinnedLocation.lng);
      formData.append("locationDisplay", pinnedLocation.display);
    }

    fetch("/api/posts", {
      method: "POST",
      body: formData,
      onUploadProgress: (progressEvent) => {
        console.log(progressEvent); // Log the event object for debugging
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100,
        );
        console.log(progress);
        setSubmissionProgress(progress);
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setSubmissionInProgress(false);
        setSubmissionProgress(0);
        setSelectedImages(new Array(8).fill(null));
        setPinnedLocation(null);
        setFormData({
          description: "",
          address: "",
          price: "",
          category: "",
        });
        localStorage.removeItem("formData"); // Clear cached form data
        // Was window.location.reload(), which threw the whole app away to show
        // the result of a POST. The page hands off to its own success state
        // instead, and the feed it links to fetches fresh on mount anyway.
        onPosted?.();
      })
      .catch((error) => {
        console.error("Error:", error);
        setSubmissionInProgress(false);
        setSubmissionProgress(0);
        fail("Something went wrong posting your report. Please try again.");
      });
  };

  return (
    <>
      <LocationPicker
        show={locationPickerShow}
        initialPin={pinnedLocation}
        defaultCity={!pinnedLocation ? selectedCity : null}
        onCancel={() => setLocationPickerShow(false)}
        onProceed={(loc) => {
          setPinnedLocation(loc);
          setLocationPickerShow(false);
        }}
      />

      <form
        className="bleep-form"
        onSubmit={postReport}
        encType="multipart/form-data"
      >
        {/* The intro banner that used to head this block is gone. It said
            "Report suspected counterfeit goods or services", which is what the
            page's own h1 and lead say directly above it. In a modal it was the
            only thing naming the dialog; on a page it was a second title. */}
        <div className="bleep-form-body">
          <Step n={1} title="Product category" />

          <input type="hidden" name="category" value={formData.category} />
          <div className="bleep-cat-dropdown mb-2" ref={catRef}>
            <button
              type="button"
              className="bleep-cat-trigger"
              onClick={() => setCatOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={catOpen}
            >
              {formData.category ? (
                <>
                  <img
                    src={`/images/categories/${formData.category.toLowerCase()}.png`}
                    alt=""
                    className="bleep-cat-trigger-icon"
                  />
                  <span>{formData.category}</span>
                </>
              ) : (
                <span className="bleep-cat-placeholder">Select category</span>
              )}
              <i
                className={`fas fa-chevron-down bleep-cat-chevron${catOpen ? " bleep-cat-chevron--open" : ""}`}
                aria-hidden="true"
              />
            </button>

            {catOpen && (
              <div
                className="bleep-cat-panel"
                role="listbox"
                aria-label="Product category"
              >
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    role="option"
                    aria-selected={formData.category === cat}
                    className={`bleep-cat-option${formData.category === cat ? " bleep-cat-option--selected" : ""}`}
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, category: cat }));
                      setCatOpen(false);
                    }}
                  >
                    <img
                      src={`/images/categories/${cat.toLowerCase()}.png`}
                      alt=""
                      className="bleep-cat-option-icon"
                    />
                    <span className="bleep-cat-option-label">{cat}</span>
                    {formData.category === cat && (
                      <i
                        className="fas fa-check bleep-cat-option-check"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Step
            n={2}
            title="Photos"
            hint="Each slot asks for a different angle. The more you can give us, the easier the item is to verify."
          />
          {/* Eight slots with only one required is not readable from the grid,
              and the old copy said so once in a line you had scrolled past by
              the time you were picking files. This tracks as you go. */}
          <p className="bleep-upload-count">
            <span>
              <strong>{photoCount}</strong> of 8 added
            </span>
            {photoCount === 0 && (
              <span className="bleep-upload-count__req">
                At least one required
              </span>
            )}
          </p>
          <div className="bleep-upload-grid">
            {selectedImages.map((image, index) => (
              <div
                key={index}
                // No `clickable` and no Bootstrap flex utilities. `clickable`
                // is an App.css class whose only rule is a 31px black glow on
                // hover, which is where that shadow was coming from; the flex
                // three were duplicating what .bleep-upload-tile already sets.
                className={`bleep-upload-tile ${
                  image
                    ? "bleep-upload-tile--filled"
                    : "bleep-upload-tile--empty"
                }`}
                role="button"
                tabIndex={0}
                aria-label={`${image ? "Change" : "Upload"} photo ${
                  index + 1
                }, ${prompts[index]}`}
                onClick={() =>
                  document.getElementById(`fileInput${index}`).click()
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById(`fileInput${index}`).click();
                  }
                }}
              >
                {image ? (
                  <>
                    <img
                      src={URL.createObjectURL(image)}
                      alt={`Preview ${index + 1}`}
                      className="bleep-upload-img"
                    />
                    {/* Not inside a hover-revealed overlay any more: on a phone
                        there is no hover, so a photo could not be removed at
                        all once it was chosen. */}
                    <button
                      type="button"
                      className="bleep-upload-remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(index);
                      }}
                      aria-label={`Remove photo ${index + 1}, ${prompts[index]}`}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  </>
                ) : (
                  <div className="bleep-upload-placeholder">
                    {/* The corner index is gone. The prompt already identifies
                        the slot, and three things in a 100px square was two
                        too many. */}
                    <i
                      className={`fas ${index === 0 ? "fa-camera" : "fa-plus"} bleep-upload-icon`}
                      aria-hidden="true"
                    />
                    <span className="bleep-upload-label">{prompts[index]}</span>
                  </div>
                )}

                <input
                  id={`fileInput${index}`}
                  type="file"
                  accept="image/png, image/gif, image/jpeg"
                  style={{ display: "none" }}
                  onChange={(event) => handleFileChange(index, event)}
                />
              </div>
            ))}
          </div>

          <Step n={3} title="What is suspicious about it?" />

          <div className="mb-2">
            <textarea
              className="form-control bleep-textarea"
              placeholder="Describe the problem with the item(s) here. Provide as much detail as possible. E.g. differences from the authentic item in terms of appearance, packaging, price, seller details etc."
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              style={{ height: "150px" }}
            ></textarea>
          </div>

          <Step n={4} title="Where did you get it?" />

          <div className="mb-2">
            <Select
              inputId="city"
              options={allCities.map((c) => ({ value: c, label: c }))}
              value={
                selectedCity
                  ? { value: selectedCity, label: selectedCity }
                  : null
              }
              onChange={handleCitySelect}
              placeholder="City / Town"
              isClearable
              styles={citySelectStyles}
              noOptionsMessage={() => "No cities found"}
            />
          </div>

          <div className="mb-2">
            <textarea
              className="form-control bleep-textarea"
              placeholder="Provide as much detail as possible, e.g. shop name, location, website, social media handle etc."
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              style={{ height: "100px" }}
            ></textarea>
          </div>

          {/* Location pin - required */}
          {pinnedLocation ? (
            <div className="bleep-location-preview mb-2">
              <div className="bleep-location-preview__left">
                <i className="fas fa-map-marker-alt bleep-location-preview__icon" />
                <div style={{ minWidth: 0 }}>
                  <span className="bleep-location-preview__label">
                    Location pinned
                  </span>
                  <span className="bleep-location-preview__text">
                    {pinnedLocation.display}
                  </span>
                </div>
              </div>
              <div className="bleep-location-preview__actions">
                <button
                  type="button"
                  className="bleep-location-preview__change"
                  onClick={() => setLocationPickerShow(true)}
                >
                  Change
                </button>
                <button
                  type="button"
                  className="bleep-location-preview__remove"
                  aria-label="Remove pinned location"
                  onClick={() => setPinnedLocation(null)}
                >
                  <i className="fas fa-times" />
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="bleep-location-btn mb-2"
              onClick={() => setLocationPickerShow(true)}
            >
              <span className="bleep-location-btn__inner">
                <i className="fas fa-map-marker-alt bleep-location-btn__icon" />
                <span>
                  <span className="bleep-location-btn__label">
                    Pin exact location on map
                  </span>
                  <span className="bleep-location-btn__sub">
                    Tap here to open map
                  </span>
                </span>
              </span>
            </button>
          )}

          <Step n={5} title="How much did you pay?" />

          <div className="input-group mb-2 bleep-input-group">
            <input
              type="text"
              className="form-control bleep-input"
              id="price"
              name="price"
              placeholder="Amount"
              value={formData.price}
              onChange={handleInputChange}
              inputMode="numeric"
              autoComplete="off"
            />
            <span className="input-group-text bleep-currency">{currency}</span>
          </div>

          <input id="country" name="country" type="hidden" value={domain} />

          {/* Inside the card, not below it. The modal pinned this to the dialog
              footer, and keeping it as a sibling of the card left the submit
              floating on the page gradient, reading as a page action rather
              than as the end of the form it belongs to. */}
          <div className="bleep-form-foot">
            <div ref={errorRef} aria-live="polite">
              {formError && (
                <div className="bl-alert bl-alert--danger bleep-form-error">
                  <i
                    className="fas fa-circle-exclamation bl-alert__icon"
                    aria-hidden="true"
                  />
                  <span>{formError}</span>
                </div>
              )}
            </div>

            {submissionInProgress && (
              <div
                className="progress bleep-form-progress"
                role="progressbar"
                aria-valuenow={submissionProgress}
                aria-valuemin="0"
                aria-valuemax="100"
              >
                {/* No percentage printed inside the bar: it was 9px white on
                    papaya, unreadable twice over. The button already reads
                    "Posting…" and aria-valuenow carries the number. */}
                <div
                  className="progress-bar bleep-form-progress__bar"
                  style={{ width: `${submissionProgress}%` }}
                />
              </div>
            )}

            {userId ? (
              <button
                type="submit"
                className="bleep-form-submit"
                disabled={submissionInProgress}
                aria-busy={submissionInProgress}
              >
                {submissionInProgress ? "Posting…" : "Post report"}
                {!submissionInProgress && (
                  <i className="fas fa-arrow-right btn-arrow" />
                )}
              </button>
            ) : (
              <Link
                to="/signin?redirect=report"
                className="text-decoration-none"
                style={{ width: "100%" }}
              >
                <button type="button" className="bleep-form-submit">
                  Sign in to bleep
                  <i className="fas fa-arrow-right btn-arrow" />
                </button>
              </Link>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default ReportForm;
